/* eslint-disable prettier/prettier */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import swal from "sweetalert";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import MDButton from "admin/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";
import DataTable from "admin/examples/Tables/DataTable";

// Data
import usersTableData from "admin/layouts/users/data/usersTableData";

// User Modal
import UserModal from "admin/layouts/users/components/UserModal";

// Service
import userService from "admin/services/userService";

function Users() {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await userService.getAllUsers();
            if (response.status && response.data) {
                setUsers(response.data);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            swal("Error", "Failed to fetch users", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleOpenAddModal = () => {
        setSelectedUser(null);
        setModalOpen(true);
    };

    const handleOpenEditModal = (user) => {
        setSelectedUser(user);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedUser(null);
    };

    const API = process.env.REACT_APP_APIHOST || "http://localhost:3033";
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteForm, setInviteForm] = useState({ firstName: "", lastName: "", email: "", company: "", title: "" });
    const [inviteSending, setInviteSending] = useState(false);

    const handleInvite = async () => {
        if (!inviteForm.firstName || !inviteForm.lastName || !inviteForm.email) {
            swal("Error", "First name, last name and email required", "error"); return;
        }
        setInviteSending(true);
        try {
            await axios.post(API + "/invite", inviteForm);
            swal("Success", "Invitation sent to " + inviteForm.email, "success");
            setInviteOpen(false);
            setInviteForm({ firstName: "", lastName: "", email: "", company: "", title: "" });
            fetchUsers();
        } catch (e) {
            swal("Error", e?.response?.data?.errorMessage || "Failed to send invite", "error");
        }
        setInviteSending(false);
    };

    const handleSaveUser = async (userData) => {
        try {
            if (selectedUser) {
                // Update existing user
                await userService.updateUser(selectedUser._id, userData);
                swal("Success", "User updated successfully", "success");
            } else {
                // Create new user
                await userService.createUser(userData);
                swal("Success", "User created successfully", "success");
            }
            fetchUsers(); // Refresh list
            handleCloseModal();
        } catch (error) {
            console.error("Error saving user:", error);
            swal("Error", error.errorMessage || "Failed to save user", "error");
        }
    };

    const handleDeleteUser = async (userId) => {
        swal({
            title: "Are you sure?",
            text: "Once deleted, you will not be able to recover this user!",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                try {
                    await userService.deleteUser(userId);
                    swal("Poof! User has been deleted!", {
                        icon: "success",
                    });
                    fetchUsers(); // Refresh list
                } catch (error) {
                    console.error("Error deleting user:", error);
                    swal("Error", error.errorMessage || "Failed to delete user", "error");
                }
            }
        });
    };

    const { columns, rows } = usersTableData(users, handleOpenEditModal, handleDeleteUser);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={2} pb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                px={2} pt={2} pb={1}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="body2" sx={{ color: "#C29717", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1.5px" }}>
                                    Users
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    sx={{ color: "#aaa", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { color: "#fff" } }}
                                    onClick={handleOpenAddModal}
                                >
                                    <Icon sx={{ fontSize: "14px !important" }}>add_circle_outline</Icon> Add user
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    sx={{ color: "#C29717", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { color: "#fff" } }}
                                    onClick={() => setInviteOpen(!inviteOpen)}
                                >
                                    <Icon sx={{ fontSize: "14px !important" }}>mail_outline</Icon> Invite
                                </MDTypography>
                            </MDBox>
                            {inviteOpen && (
                            <MDBox px={2} py={1.5} sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(194,151,23,0.02)" }}>
                                <MDTypography variant="body2" sx={{ color: "#C29717", fontSize: "11px", mb: 1.5 }}>Send invitation (no password required)</MDTypography>
                                <MDBox display="flex" gap={1} flexWrap="wrap" mb={1}>
                                    <input placeholder="First name *" value={inviteForm.firstName} onChange={e => setInviteForm({...inviteForm, firstName: e.target.value})}
                                        style={{ flex: "1 1 120px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "8px 12px", color: "#e6e6e6", fontSize: 12, outline: "none" }} />
                                    <input placeholder="Last name *" value={inviteForm.lastName} onChange={e => setInviteForm({...inviteForm, lastName: e.target.value})}
                                        style={{ flex: "1 1 120px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "8px 12px", color: "#e6e6e6", fontSize: 12, outline: "none" }} />
                                    <input placeholder="Email *" type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})}
                                        style={{ flex: "1 1 180px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "8px 12px", color: "#e6e6e6", fontSize: 12, outline: "none" }} />
                                </MDBox>
                                <MDBox display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                                    <input placeholder="Company" value={inviteForm.company} onChange={e => setInviteForm({...inviteForm, company: e.target.value})}
                                        style={{ flex: "1 1 150px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "8px 12px", color: "#e6e6e6", fontSize: 12, outline: "none" }} />
                                    <input placeholder="Title" value={inviteForm.title} onChange={e => setInviteForm({...inviteForm, title: e.target.value})}
                                        style={{ flex: "1 1 150px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "8px 12px", color: "#e6e6e6", fontSize: 12, outline: "none" }} />
                                    <span onClick={handleInvite} style={{ padding: "8px 18px", background: "rgba(194,151,23,0.15)", border: "1px solid rgba(194,151,23,0.3)", borderRadius: 3, color: "#C29717", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                                        {inviteSending ? "Sending..." : "Send invite"}
                                    </span>
                                </MDBox>
                            </MDBox>
                            )}
                            <MDBox px={2} py={1}>
                                {loading ? (
                                    <MDBox display="flex" justifyContent="center" p={3}>
                                        <div className="forte-spin" style={{margin:"0 auto"}}></div>
                                    </MDBox>
                                ) : users.length === 0 ? (
                                    <MDTypography sx={{ color: "#888", fontSize: "13px", py: 2, textAlign: "center" }}>No users</MDTypography>
                                ) : (
                                    users.map(u => (
                                        <MDBox key={u._id} sx={{ borderBottom: "1px solid rgba(255,255,255,0.04)", py: 1.2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <MDBox>
                                                <MDTypography sx={{ color: "#fff", fontSize: "13px", fontWeight: 500, mb: 0.3 }}>{u.firstName} {u.lastName}</MDTypography>
                                                <MDBox display="flex" gap={1} alignItems="center">
                                                    <span style={{ fontSize: "11px", color: "#888" }}>{u.email}</span>
                                                    <span style={{ fontSize: "9px", color: "#C29717", background: "rgba(194,151,23,0.1)", border: "1px solid rgba(194,151,23,0.15)", borderRadius: "2px", padding: "0 5px" }}>{u.role}</span>
                                                        {u.inviteToken && <span style={{ fontSize: "9px", color: "#e88a2a", background: "rgba(232,138,42,0.1)", border: "1px solid rgba(232,138,42,0.15)", borderRadius: "2px", padding: "0 5px", marginLeft: 4 }}>pending invite</span>}
                                                        {!u.inviteToken && u.passwordSetAt && !u.lastLoginAt && <span style={{ fontSize: "9px", color: "#7a9ec2", background: "rgba(122,158,194,0.1)", border: "1px solid rgba(122,158,194,0.15)", borderRadius: "2px", padding: "0 5px", marginLeft: 4 }}>password set</span>}
                                                        {u.lastLoginAt && <span style={{ fontSize: "9px", color: "#4CAF50", background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.15)", borderRadius: "2px", padding: "0 5px", marginLeft: 4 }}>active</span>}
                                                    <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "1px", background: u.status === "active" ? "#4CAF50" : "#555" }}></span>
                                                </MDBox>
                                                {u.company && <MDTypography sx={{ color: "#666", fontSize: "10px", mt: 0.3 }}>{u.company}{u.companyLocation ? " - " + u.companyLocation : ""}</MDTypography>}
                                            </MDBox>
                                            <MDBox display="flex" gap={0.8} flexShrink={0}>
                                                <Icon sx={{ fontSize: "14px !important", color: "#888", cursor: "pointer", "&:hover": { color: "#fff" } }} onClick={() => handleOpenEditModal(u)}>edit</Icon>
                                                <Icon sx={{ fontSize: "14px !important", color: "#888", cursor: "pointer", "&:hover": { color: "#e55" } }} onClick={() => handleDeleteUser(u._id)}>delete</Icon>
                                            </MDBox>
                                            <MDBox display="flex" gap={1.5} flexWrap="wrap" px={0} mt={0.3}>
                                                {u.invitedBy && <span style={{ fontSize: "9px", color: "#888" }}>Invited by {u.invitedBy.firstName} {(u.invitedBy.lastName||"").charAt(0)}.</span>}
                                                {u.loginCount > 0 && <span style={{ fontSize: "9px", color: "#4CAF50" }}>Logins: {u.loginCount}</span>}
                                                {u.lastLoginAt && <span style={{ fontSize: "9px", color: "#555" }}>Last: {new Date(u.lastLoginAt).toLocaleDateString("fr-FR", {day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span>}
                                                {u.passwordSetAt && <span style={{ fontSize: "9px", color: "#555" }}>Pwd set: {new Date(u.passwordSetAt).toLocaleDateString("fr-FR", {day:"2-digit",month:"short",year:"numeric"})}</span>}
                                                {u.createdAt && <span style={{ fontSize: "9px", color: "#555" }}>Created: {new Date(u.createdAt).toLocaleDateString("fr-FR", {day:"2-digit",month:"short",year:"numeric"})}</span>}
                                            </MDBox>
                                        </MDBox>
                                    ))
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <UserModal
                open={modalOpen}
                onClose={handleCloseModal}
                user={selectedUser}
                onSave={handleSaveUser}
            />
        </DashboardLayout>
    );
}

export default Users;
